<script setup lang="ts">
import { computed, inject, ref } from "vue"
import { storeToRefs } from "pinia"
import { ALButton, ALDropdownMenu, ALIconButton } from "@af-lib/vue-ui"

import NotPassedAssessment from "@/components/af/NotPassedAssessment/NotPassedAssessment.vue"
import RulesModal from "@/components/af/RulesModal/RulesModal.vue"
import LangMenu from "@/components/af/LangMenu/LangMenu.vue"

import { useHeaderControlsStore } from "@/components/af/HeaderControls/HeaderControls.store"
import { useHelpStore } from "@/components/af/Help/help.store"
import { useHelpAudio } from "@/components/af/Help/useHelpAudio"
import { useRulesModalStore } from "@/components/af/RulesModal/rulesModal.store"
import { useUserStore } from "@/stores/user"
import type { Translate } from "@/plugins/i18n/types"

const t = inject("t") as Translate

const helpStore = useHelpStore()
const { isActiveAudioBox, isVolumeActive, name } = storeToRefs(helpStore)
const { getIsAssessmentNotStarted } = helpStore

const { isFromInt } = storeToRefs(useUserStore())
const { handleNotPassed, openNotPassedModal } = useHeaderControlsStore()
const { handleAudio } = useHelpAudio()

const rulesModalStore = useRulesModalStore()
const { openRulesModal } = rulesModalStore
const { hasRules } = storeToRefs(rulesModalStore)

const isOpen = ref(false)

const dropdownItems = computed(() => [
  ...(getIsAssessmentNotStarted()
    ? [
        {
          value: "not-passed",
          label: t("assessment.navbar.accessibility.skip_assessment"),
          icon: "fat fa-universal-access",
        },
      ]
    : []),
  ...(isActiveAudioBox.value
    ? [
        {
          value: "audio",
          label: isVolumeActive.value
            ? t("assessment.navbar.accessibility.audio_disable")
            : t("assessment.navbar.accessibility.audio_enable"),
          icon: isVolumeActive.value ? "fat fa-volume" : "fat fa-volume-slash",
        },
      ]
    : []),
])

const handleItemClick = (value: string) => {
  if (value === "not-passed") openNotPassedModal()
  else if (value === "audio") handleAudio()
  else if (value === "rules") openRulesModal()
}
</script>

<template>
  <ALDropdownMenu
    v-if="dropdownItems.length > 0"
    v-model:is-open="isOpen"
    :options="dropdownItems"
    @on-click="handleItemClick"
  >
    <template #trigger>
      <div>
        <!-- Mobile Accessibility icon button -->
        <ALIconButton
          v-if="hasRules"
          variant="secondary"
          :label="t('assessment.navbar.accessibility.title')"
          icon="fa-universal-access"
          class="lg:hidden"
        />
        <!-- Desktop Accessibility button -->
        <ALButton type="secondary" class="hidden! lg:inline-flex!">
          <FaIcon icon="fas fa-universal-access" class="text-lg" />
          {{ t("assessment.navbar.accessibility.title") }}
          <FaIcon
            icon="fas fa-chevron-down"
            class="transition-transform duration-300"
            :class="{ 'rotate-180': isOpen }"
          />
        </ALButton>
      </div>
    </template>
  </ALDropdownMenu>

  <RulesModal />
  <ALIconButton
    v-if="hasRules"
    variant="secondary"
    :label="
      t('assessment.navbar.rules.tooltip', { assessment: name.toUpperCase() })
    "
    icon="fas fa-info-circle"
    @click="handleItemClick('rules')"
  />
  <NotPassedAssessment
    v-if="getIsAssessmentNotStarted()"
    @onNotPassed="handleNotPassed"
  />

  <LangMenu v-if="!isFromInt" />
</template>
